import React from 'react';

import Select from '@uikit/Form/Select/Select';
import Radio from '@uikit/old/Radio/Radio';
import BillingFormField from '@components/Publisher/Settings/SettingsBillingDetails/_components/BillingFormField/BillingFormField';

import { PayoutFormField } from 'respona_api/generated/billing-publisher_pb';
import { PayoutFormFieldType } from '@ts/billing-publisher.types';

type Props = {
  field: PayoutFormFieldType;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  error?: string;
};

export const validatePayoutField = (
  field: PayoutFormFieldType,
  value: string
): string | undefined => {
  const trimmed = (value ?? '').trim();
  if (!trimmed) {
    return field.required ? `${field.label} is required` : undefined;
  }
  if (field.minLength > 0 && trimmed.length < field.minLength) {
    return `${field.label}: at least ${field.minLength} characters`;
  }
  if (field.maxLength > 0 && trimmed.length > field.maxLength) {
    return `${field.label}: at most ${field.maxLength} characters`;
  }
  if (field.validationRegexp) {
    try {
      const re = new RegExp(field.validationRegexp);
      if (!re.test(trimmed)) {
        return field.example
          ? `Invalid ${field.label.toLowerCase()}. Example: ${field.example}`
          : `Invalid ${field.label.toLowerCase()}`;
      }
    } catch {
      // server sent an invalid regex; skip pattern validation
    }
  }
  return undefined;
};

function PayoutFieldRenderer({ field, value, onChange, onBlur, error }: Props): JSX.Element {
  const placeholder = field.example || field.label;

  if (field.type === PayoutFormField.FieldType.SELECT) {
    const options = field.optionsList.map((o) => ({ value: o.key, label: o.label }));
    return (
      <BillingFormField label={field.label} isError={!!error} errorMessage={error}>
        <Select
          options={options}
          value={options.find((o) => o.value === value) || null}
          onChange={(val: { value: string; label: string } | null) => onChange(val?.value || '')}
          placeholder={placeholder}
          menuPlacement="auto"
        />
      </BillingFormField>
    );
  }

  if (field.type === PayoutFormField.FieldType.RADIO) {
    return (
      <BillingFormField label={field.label} isError={!!error} errorMessage={error}>
        <div className="payout-field-radio-group">
          {field.optionsList.map((o) => (
            <Radio
              key={o.key}
              value={o.key}
              isChecked={value === o.key}
              onChange={() => onChange(o.key)}
            >
              {o.label}
            </Radio>
          ))}
        </div>
      </BillingFormField>
    );
  }

  if (field.type === PayoutFormField.FieldType.DATE) {
    return (
      <BillingFormField label={field.label} isError={!!error} errorMessage={error}>
        <input
          type="date"
          className="payout-field-date-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      </BillingFormField>
    );
  }

  return (
    <BillingFormField
      label={field.label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      isError={!!error}
      errorMessage={error}
      maxLength={field.maxLength > 0 ? field.maxLength : undefined}
    />
  );
}

export default PayoutFieldRenderer;
